import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { TextReveal } from "@/components/ui/TextReveal";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  Brain, 
  FileText, 
  Link2, 
  RefreshCw, 
  Check, 
  X, 
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Shield,
  Globe,
  Zap,
  ArrowRight,
  MessageSquare,
  BarChart3,
  Building2
} from "lucide-react";

const frameworkSteps = [
  {
    number: "01",
    title: "AI Search Visibility Audit",
    icon: Search,
    description: "We analyze where (and if) your brand appears in AI answers, how competitors are being referenced, and gaps between your SEO presence and AI visibility."
  },
  {
    number: "02",
    title: "Entity & Authority Structuring",
    icon: Brain,
    description: "AI systems don't rank pages — they understand entities. We define your brand, services, and expertise clearly, structure content so LLMs can interpret relationships, and eliminate ambiguity."
  },
  {
    number: "03",
    title: "Content Built for Humans and AI",
    icon: FileText,
    description: "We don't publish blog spam. We create high-signal pages with clear intent, structured explanations AI can summarize correctly, and content that converts once traffic arrives."
  },
  {
    number: "04",
    title: "Citation & Trust Signal Expansion",
    icon: Link2,
    description: "AI answers are trained and validated across the web. We ensure your brand is present in trusted sources, referenced consistently, and reinforced across platforms AI models learn from."
  },
  {
    number: "05",
    title: "Continuous Optimization",
    icon: RefreshCw,
    description: "AI search is evolving weekly. We monitor AI mentions and citations, adapt structure as models change, and improve visibility without rebuilding everything."
  }
];

const idealClients = [
  "Sell B2B, SaaS, or cloud solutions",
  "Have longer sales cycles",
  "Care about pipeline quality, not vanity traffic",
  "Want visibility that compounds over time"
];

const notFor = [
  "Local SEO hacks",
  "Short-term traffic spikes",
  "Content mills or affiliate plays"
];

const differentiators = [
  { icon: Sparkles, title: "AI-first from the ground up", description: "Not traditional SEO with AI tools bolted on" },
  { icon: Target, title: "Lean by design", description: "Senior-level thinking without agency bloat" },
  { icon: Zap, title: "Systems over tactics", description: "We design search systems, not run campaigns" },
  { icon: TrendingUp, title: "Outcomes over activity", description: "Focus on pipeline impact, not vanity metrics" },
  { icon: Globe, title: "Global presence", description: "Distributed team across US, UK, UAE, AUS" },
  { icon: Shield, title: "No hidden upsells", description: "Transparent pricing, no lock-ins" }
];

const results = [
  { icon: MessageSquare, label: "Brand mentions inside AI answers", value: "AI Visibility" },
  { icon: Users, label: "Higher-intent organic traffic", value: "Quality Traffic" },
  { icon: Building2, label: "Faster trust during sales conversations", value: "Sales Velocity" },
  { icon: BarChart3, label: "Reduced dependency on paid ads", value: "Cost Efficiency" }
];

const AISEOAgency = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI SEO Agency Services",
    "provider": {
      "@type": "Organization",
      "name": "TheCloudSEO"
    },
    "description": "AI-first SEO agency helping B2B, SaaS, and cloud companies stay visible in AI search through Generative Engine Optimization (GEO).",
    "areaServed": ["US", "UK", "UAE", "Australia"],
    "serviceType": "AI SEO and Generative Engine Optimization"
  };

  return (
    <Layout>
      <SEO 
        title="AI SEO Agency | Generative Engine Optimization | TheCloudSEO"
        description="Get discovered where modern buyers actually search — ChatGPT, AI answers, and Google combined. AI-first SEO agency for B2B, SaaS, and cloud companies."
        canonical="https://thecloudseo.com/ai-seo-agency"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-24 pb-16">
        {/* Animated Background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI SEO Agency
              </span>
            </AnimatedSection>

            <TextReveal delay={0.1}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                AI SEO Agency Built for the{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  AI Search Era
                </span>
              </h1>
            </TextReveal>

            <AnimatedSection delay={0.2}>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Get discovered where modern buyers actually search — ChatGPT, AI answers, and Google combined.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8 py-6" asChild>
                  <Link to="/contact">
                    Book Your AI Growth Audit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6" asChild>
                  <a href="#framework">Learn Our Process</a>
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="grid md:grid-cols-2 gap-8">
                {/* The Shift Card */}
                <motion.div 
                  className="bg-card border border-border rounded-2xl p-8"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-2xl md:text-3xl font-semibold leading-relaxed">
                    <span className="text-muted-foreground">Traditional SEO was built for blue links.</span>
                    <br />
                    <span className="text-primary">Modern buyers start with AI answers.</span>
                  </p>
                </motion.div>

                {/* Key Points Card */}
                <motion.div 
                  className="bg-card border border-border rounded-2xl p-8"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-sm text-primary font-medium mb-4 uppercase tracking-wider">Today's Reality</p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      <span className="text-lg">Buyers ask ChatGPT before Google</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      <span className="text-lg">AI summaries replace top-of-page rankings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      <span className="text-lg">"Ranking #1" doesn't guarantee visibility anymore</span>
                    </li>
                  </ul>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* What Traditional SEO Gets Wrong */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Traditional SEO No Longer Works Alone
              </h2>
              <p className="text-xl text-muted-foreground mb-12">
                Search has changed — quietly, but permanently.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="bg-card border border-border rounded-2xl p-8 md:p-12">
                <p className="text-muted-foreground mb-6">Most SEO agencies are still optimizing for:</p>
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  {["Keywords", "Blog volume", "Link counts"].map((item) => (
                    <span key={item} className="px-4 py-2 bg-destructive/10 text-destructive rounded-full text-sm font-medium">
                      {item}
                    </span>
                  ))}
                </div>
                <p className="text-xl font-semibold text-primary">
                  That's not how AI systems discover, understand, or recommend brands.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* What an AI SEO Agency Does */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  What an AI SEO Agency Actually Does
                </h2>
                <p className="text-xl text-muted-foreground">
                  AI SEO isn't "SEO with AI tools." It's a structural shift in how search visibility works.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                {[
                  { icon: Brain, text: "How AI models understand entities" },
                  { icon: MessageSquare, text: "How answers are generated" },
                  { icon: Shield, text: "Which sources AI trusts and cites" },
                  { icon: TrendingUp, text: "How users move from AI answers to buying decisions" }
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-lg">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="text-center">
                <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Sparkles className="w-4 h-4" />
                  This is Generative Engine Optimization (GEO) — layered on top of strong SEO fundamentals.
                </span>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* AI SEO Framework */}
      <section id="framework" className="py-20 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Our AI SEO Framework
                </h2>
                <p className="text-xl text-muted-foreground">
                  We don't run campaigns. We design search systems.
                </p>
              </div>
            </AnimatedSection>

            <div className="space-y-6">
              {frameworkSteps.map((step, index) => (
                <AnimatedSection key={step.number} delay={index * 0.1}>
                  <motion.div 
                    className="group relative bg-card border border-border rounded-2xl p-6 md:p-8 hover:border-primary/50 transition-colors"
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex items-center gap-4">
                        <span className="text-4xl font-bold text-primary/30 group-hover:text-primary/50 transition-colors">
                          {step.number}
                        </span>
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                          <step.icon className="w-7 h-7 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                        <p className="text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Who This AI SEO Service Is For
              </h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-8">
              <AnimatedSection delay={0.1}>
                <div className="bg-card border border-primary/30 rounded-2xl p-8 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">We work best with companies that:</h3>
                  </div>
                  <ul className="space-y-4">
                    {idealClients.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-lg">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-card border border-border rounded-2xl p-8 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                      <X className="w-5 h-5 text-destructive" />
                    </div>
                    <h3 className="text-xl font-semibold">This is not for:</h3>
                  </div>
                  <ul className="space-y-4">
                    {notFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                        <span className="text-lg text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  What Results Look Like
                </h2>
                <p className="text-xl text-muted-foreground">
                  AI SEO doesn't show up as "rank #3" in a report. Instead, clients see:
                </p>
              </div>
            </AnimatedSection>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {results.map((result, i) => (
                <AnimatedSection key={i} delay={i * 0.1}>
                  <motion.div 
                    className="text-center p-6 bg-card border border-border rounded-xl"
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <result.icon className="w-7 h-7 text-primary" />
                    </div>
                    <p className="text-lg font-semibold mb-2">{result.value}</p>
                    <p className="text-sm text-muted-foreground">{result.label}</p>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={0.4}>
              <p className="text-xl text-center font-semibold text-primary">
                Search visibility that actually influences buying decisions.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Why TheCloudSEO Is Different */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Why TheCloudSEO Is Different
                </h2>
                <p className="text-xl text-muted-foreground">
                  Most agencies add AI tools. We redesign the system.
                </p>
              </div>
            </AnimatedSection>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {differentiators.map((diff, i) => (
                <AnimatedSection key={i} delay={i * 0.1}>
                  <motion.div 
                    className="p-6 bg-card border border-border rounded-xl h-full"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <diff.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{diff.title}</h3>
                    <p className="text-muted-foreground">{diff.description}</p>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Model */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Engagement Model
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                AI SEO is not a one-off task.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <motion.div 
                className="bg-card border border-primary/30 rounded-2xl p-8 md:p-12"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-muted-foreground mb-4">3-month minimum engagements, starting at</p>
                <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
                  $<AnimatedCounter end={2000} duration={2} />
                </div>
                <p className="text-xl text-muted-foreground mb-8">/ month</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8 text-left max-w-md mx-auto">
                  {["Strategy", "Implementation", "Ongoing optimization", "Transparent reporting"].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    No long-term lock-ins
                  </span>
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    No hidden upsells
                  </span>
                </div>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Start With an AI Search Audit
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                If you're unsure where you stand in AI search today, start here.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="bg-card border border-border rounded-2xl p-8 mb-8">
                <p className="text-muted-foreground mb-6">A free AI Growth Audit will show:</p>
                <ul className="space-y-3 text-left max-w-md mx-auto mb-8">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>How visible your brand is in AI answers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>Where competitors are winning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>What to fix first for maximum impact</span>
                  </li>
                </ul>

                <Button size="lg" className="text-lg px-8 py-6" asChild>
                  <Link to="/contact">
                    Book Your AI Growth Audit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <p className="text-lg text-muted-foreground italic">
                Build visibility that survives the next search shift — not just the last one.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AISEOAgency;
